import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiClient } from '@/lib/apiClient';
import type { ApiResponse, Conversation, Message } from '@/types/api';
import { useWebSocket } from './useWebSocket';
import { USE_MOCKS } from '@/lib/mockConfig';
import {
  mockConversations,
  mockMessages,
  mockProfiles,
  type CustomerProfile,
} from '@/lib/mockData';

interface UseConversationsOptions {
  search?: string;
  platform?: string;
  status?: string;
  page?: number;
  limit?: number;
}

const filterMockConversations = (opts: UseConversationsOptions) => {
  const term = (opts.search || '').trim().toLowerCase();
  return mockConversations.filter((c) => {
    if (opts.platform && c.platform !== opts.platform) return false;
    if (opts.status && c.status !== opts.status) return false;
    if (term && !`${c.customerName} ${c.lastMessage}`.toLowerCase().includes(term)) return false;
    return true;
  });
};

export const useConversations = (options: UseConversationsOptions = {}) => {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();
  const { search, platform, status } = options;

  const query = useQuery({
    queryKey: ['conversations', { search, platform, status }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (platform) params.append('platform', platform);
      if (status) params.append('status', status);

      const mockFallback = () => filterMockConversations(options);

      return apiClient.get<ApiResponse<Conversation[]>>(`/api/conversations?${params}`)
        .then((res) => {
          const list = res.data.data || [];
          if (USE_MOCKS && list.length === 0) return mockFallback();
          return list;
        })
        .catch((err) => {
          if (USE_MOCKS) return mockFallback();
          throw err;
        });
    },
    staleTime: 30 * 1000,
  });

  // Live: refresh the list when a conversation is updated or a new message lands.
  useEffect(() => {
    const off1 = subscribe('conversation_updated', () =>
      queryClient.invalidateQueries({ queryKey: ['conversations'] }),
    );
    const off2 = subscribe('new_message', () =>
      queryClient.invalidateQueries({ queryKey: ['conversations'] }),
    );
    return () => {
      off1();
      off2();
    };
  }, [subscribe, queryClient]);

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiClient.patch<ApiResponse<{ id: string; status: string }>>(`/api/conversations/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversations'] }),
    onError: () => toast.error('Failed to update conversation'),
  });

  return {
    conversations: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    updateConversation: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  } as const;
};

/** Single conversation lookup — powers the chat header. */
export const useConversation = (conversationId: string | null) => {
  const { conversations, updateConversation, isUpdating } = useConversations();
  const conversation = useMemo(
    () => (conversationId ? conversations.find((c: Conversation) => c.id === conversationId) ?? null : null),
    [conversationId, conversations],
  );
  return { conversation, isLoading: false, updateConversation, isUpdating } as const;
};

export const useMessages = (conversationId: string) => {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();
  const [messages, setMessages] = useState<Message[]>([]);

  const query = useQuery({
    queryKey: ['messages', conversationId],
    enabled: !!conversationId,
    queryFn: () =>
      apiClient.get<ApiResponse<Message[]>>(`/api/conversations/${conversationId}/messages`)
        .then((res) => {
          const list = res.data.data || [];
          if (USE_MOCKS && list.length === 0) return mockMessages[conversationId] ?? [];
          return list;
        })
        .catch((err) => {
          if (USE_MOCKS) return mockMessages[conversationId] ?? [];
          throw err;
        }),
    staleTime: 15 * 1000,
  });

  // Keep a local copy so optimistic sends + live inbound append instantly.
  useEffect(() => {
    setMessages(query.data ?? []);
  }, [query.data]);

  // Live: append inbound messages that belong to this open conversation.
  useEffect(() => {
    if (!conversationId) return;
    const off = subscribe('new_message', (data: any) => {
      const incoming: Message | undefined = data?.message ?? data;
      if (incoming && incoming.conversationId === conversationId) {
        setMessages((prev) =>
          prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming],
        );
      }
    });
    return () => off();
  }, [conversationId, subscribe]);

  const sendMutation = useMutation({
    mutationFn: ({ content, type = 'text' }: { content: string; type?: string }) =>
      apiClient.post<ApiResponse<Message>>(`/api/conversations/${conversationId}/messages`, { content, type })
        .then((res) => res.data.data as Message),
    onMutate: async ({ content, type = 'text' }) => {
      // Optimistic append so the bubble shows immediately.
      const optimistic: Message = {
        id: `tmp-${Math.random().toString(36).slice(2)}`,
        conversationId,
        content,
        direction: 'outbound',
        timestamp: new Date().toISOString(),
        status: 'sent',
        type: (type as any) || 'text',
      };
      setMessages((prev) => [...prev, optimistic]);
      return { optimisticId: optimistic.id };
    },
    onSuccess: (saved, _vars, ctx) => {
      // Swap the temp bubble for the persisted one.
      setMessages((prev) => prev.map((m) => (m.id === ctx?.optimisticId ? saved : m)));
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (_e, _vars, ctx) => {
      setMessages((prev) => prev.filter((m) => m.id !== ctx?.optimisticId));
      toast.error('Failed to send message');
    },
  });

  const sendMessage = useCallback(
    (args: { content: string; type?: string }) => {
      if (!args.content.trim()) return;
      sendMutation.mutate(args);
    },
    [sendMutation],
  );

  // Approve an AI draft (auto-reply-off mode): optionally edited, then sent.
  const approveMutation = useMutation({
    mutationFn: ({ messageId, content }: { messageId: string; content?: string }) =>
      apiClient.post<ApiResponse<Message>>(`/api/conversations/${conversationId}/messages/${messageId}/approve`, { content })
        .then((res) => res.data.data as Message),
    onSuccess: (saved) => {
      setMessages((prev) => prev.map((m) => (m.id === saved.id ? saved : m)));
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success(saved.status === 'sent' ? 'Reply sent to customer' : 'Send failed — check connection');
    },
    onError: () => toast.error('Failed to send draft'),
  });

  const discardMutation = useMutation({
    mutationFn: (messageId: string) =>
      apiClient.delete(`/api/conversations/${conversationId}/messages/${messageId}`),
    onMutate: (messageId: string) => {
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    },
    onError: () => toast.error('Failed to discard draft'),
  });

  const approveDraft = useCallback(
    (messageId: string, content?: string) => approveMutation.mutate({ messageId, content }),
    [approveMutation],
  );
  const discardDraft = useCallback((messageId: string) => discardMutation.mutate(messageId), [discardMutation]);

  return {
    messages,
    isLoading: query.isLoading,
    sendMessage,
    isSending: sendMutation.isPending,
    approveDraft,
    discardDraft,
    isApproving: approveMutation.isPending,
  } as const;
};

export const useCustomerProfile = (conversationId: string) => {
  const profile = useMemo<CustomerProfile | null>(
    () => (USE_MOCKS ? mockProfiles[conversationId] ?? null : null),
    [conversationId],
  );
  return { profile, isLoading: false } as const;
};
